import React from 'react';
import { FieldCompatibleProps } from '../Field';
import { HelpText } from '../HelpText';
import { Intent, useFullTheme } from '~/design';
import { InternalTextInput, Label } from '~/internal';
import { useId } from '~/utils';

export interface TextareaProps extends FieldCompatibleProps<string, HTMLInputElement> {
  /** The aria-decsribed-by */
  'aria-describedby'?: string;

  /** The aria-label of the textarea. */
  'aria-label'?: string;

  /** If true, the textarea cannot be interacted with. */
  disabled?: boolean;

  /** The intent of the textarea. Maps to color. */
  intent?: Intent;

  /** The size of the textarea. */
  size?: 'sm' | 'md' | 'lg';
}

export const Textarea = React.forwardRef<HTMLInputElement, TextareaProps>(function Textarea(
  {
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    disabled = false,
    helpText,
    intent = 'secondary',
    invalid,
    label = '',
    onBlur,
    onChange,
    required,
    size = 'md',
    value = '',
  },
  ref,
) {
  if (!intent) {
    throw new Error('"intent" prop must be supplied.');
  }
  if (!ariaLabel && !label) {
    throw new Error('Either "aria-label" prop or "label" prop must be supplied.');
  }

  const theme = useFullTheme();
  const id = useId();
  const adjustedIntent = invalid ? 'negative' : intent;
  const helpTextId = `${id}-helptext`;

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing[2],
      }}
    >
      <Label disabled={disabled} htmlFor={id} label={label} required={required} />

      <InternalTextInput
        aria-describedby={
          helpText ? [helpTextId, ariaDescribedBy].filter(Boolean).join(' ') : ariaDescribedBy
        }
        aria-label={label ? undefined : ariaLabel}
        disabled={disabled}
        id={id}
        intent={intent}
        invalid={invalid}
        onBlur={onBlur}
        onChange={e => onChange(e.target.value)}
        ref={ref}
        required={required}
        rows={3}
        size={size}
        type="textarea"
        value={value}
      />

      {helpText && (
        <HelpText
          id={helpTextId}
          intent={adjustedIntent}
          showIcon={invalid || disabled || intent !== 'secondary'}
        >
          {helpText}
        </HelpText>
      )}
    </div>
  );
});
